/**
 * "Change the time" for a live that has not started: the day, start, length and
 * zone, saved together. The board's strip, the webinar's live card and its row
 * on the Webinars list all open this one popover, each from its own button.
 * Before a one-off starts, Repeats is here too: a repeat makes it a series
 * (prototype `recurring-lives.html`, screen `h-convert`).
 */
import { useEffect, useRef, useState } from "react";
import { patternProblem, type SeriesPattern } from "../../shared/live-series";
import { wallClock } from "../../shared/live-time";
import type { BoardLive, BoardLiveTimeRequest } from "../../shared/types";
import { ApiError, api } from "../lib/api";
import { startAt, startPassed } from "./liveForm";
import { LiveWhenFields, type LiveWhenValue } from "./LiveWhen";
import { RepeatPicker } from "./RepeatPicker";
import { followDay } from "./repeats";
import { seatsHead, toldLine } from "./series";

/** What the host is told once the new time is saved. */
const TIME_CHANGED = "The time is changed. The page shows the new time.";

/** What the host is told once the time is saved: a one-off made a series says so. */
export function timeSavedLine(live: BoardLive): string {
  return live.series ? `It's a series now.${toldLine(live.registered)}` : TIME_CHANGED;
}

/** The time a live has now, which the popover starts from. */
type LiveTimeNow = Pick<BoardLive, "startsAt" | "timezone" | "lengthMin">;

/**
 * Save a live's new time: the time it now has, or why it did not move. With
 * `repeats` it becomes a series from that time; its people stay in for every
 * session, so there is nothing for the host to keep or put back.
 */
export async function moveLiveTime(
  webinarId: string,
  when: BoardLiveTimeRequest,
  repeats?: SeriesPattern,
): Promise<BoardLive | string> {
  try {
    if (!repeats) return await api.board.moveLive(webinarId, when);
    const made = await api.board.changeSeries(webinarId, {
      ...when,
      ...repeats,
      keepChanged: true,
      replayEmail: false,
    });
    return made.board;
  } catch (e) {
    return e instanceof ApiError ? e.message : "That time did not save. Try again.";
  }
}

/** "12 people have a seat. We'll email them the new time." Empty when nobody has one. */
function seatsLine(n: number): string {
  const who = seatsHead(n);
  return who ? `${who} We'll email them the new time.` : "";
}

/** The time as it stands, in the controls' own terms. */
function whenOf(live: LiveTimeNow): LiveWhenValue {
  const clock = wallClock(live.startsAt, live.timezone);
  return { date: clock?.date ?? "", time: clock?.time ?? "", lengthMin: live.lengthMin, timezone: live.timezone };
}

export function ChangeTime({
  live,
  seats,
  onSave,
  onClose,
  align = "start",
}: {
  live: LiveTimeNow;
  /** People with a seat, who are emailed the new time. */
  seats: number;
  /** Resolves to "" once it moved, or to why it did not. `repeats` only when it is to become a series. */
  onSave: (when: BoardLiveTimeRequest, repeats?: SeriesPattern) => Promise<string>;
  onClose: () => void;
  /** Which edge of the button's area it lines up with. */
  align?: "start" | "end";
}) {
  const [draft, setDraft] = useState<LiveWhenValue>(() => whenOf(live));
  const [pattern, setPattern] = useState<SeriesPattern | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const passed = startPassed(draft);
  const series = pattern !== null;
  const problem = pattern && draft.date ? patternProblem(pattern, draft.date) : null;
  const ready = startAt(draft) !== null && !passed && problem === null;
  const told =
    !series
      ? seatsLine(seats)
      : seatsHead(seats) && `${seatsHead(seats)} They stay in for every session and get one email: it repeats now.`;

  // Opened from the last row of a list, it can start below the fold.
  useEffect(() => {
    box.current?.scrollIntoView({ block: "nearest" });
  }, []);

  useEffect(() => {
    // A dropdown's open list lives outside the popover, at the end of the page.
    const listOpen = () => document.querySelector(".fr-select__list, .rpk-menu, .scrim, .ph-sheet-scrim") !== null;
    // The button that opened it sits beside it, so a press there is not "away".
    const away = (e: MouseEvent) => {
      const area = box.current?.parentElement;
      const target = e.target as Element;
      if (area && !area.contains(target) && !target.closest?.(".fr-select__list, .rpk-menu, .scrim, .ph-sheet-scrim")) onClose();
    };
    const key = (e: KeyboardEvent) => {
      // The month picker and an open dropdown close on their own Escape first.
      if (e.key === "Escape" && !box.current?.querySelector(".lcal") && !listOpen()) onClose();
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    setError("");
    const refused = pattern ? await onSave(draft, pattern) : await onSave(draft);
    setSaving(false);
    if (refused) setError(refused);
    else onClose();
  };

  return (
    <div className={align === "end" ? "ltime end" : "ltime"} role="dialog" aria-label="Change the time" ref={box}>
      <p className="leye">Change the time</p>
      <LiveWhenFields
        value={draft}
        keptLength={live.lengthMin}
        onChange={(patch) => {
          setDraft((was) => ({ ...was, ...patch }));
          if (patch.date) setPattern((was) => (was ? followDay(was, draft.date, patch.date ?? "") : was));
        }}
      />
      <div className="lrep">
        <RepeatPicker chip when={draft} value={pattern} onChange={setPattern} />
      </div>
      {passed ? <p className="lwarn">That time has already passed. Pick a later one.</p> : null}
      {problem && !passed ? <p className="lwarn">{problem}</p> : null}
      {error ? <p className="lwarn">{error}</p> : null}
      {told ? <p className="ltold">{told}</p> : null}
      <div className="lacts">
        <button type="button" className="lquiet" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="lsave" disabled={!ready || saving} onClick={() => void save()}>
          {saving ? "Saving…" : series ? "Make it a series" : "Save the time"}
        </button>
      </div>
    </div>
  );
}
